/** Items unique to the itinerary screen. Spec §2.15-2.17. */
import type { ItineraryItem, TimelinePosition } from '../mappers/itineraryDetails';
import { imageUrl } from '../images';
import { AndroidText } from './Items';
import { BODY_MARGINS, marginStyle } from './margins';

export interface ItineraryItemContext {
  imagesBase: string;
  imageWidthPx: number;
}

/** item_body.xml — a single TextView.
 *
 * The XML declares Body2.Dark, but BodyDelegate calls setTextAppearance(item.style) on
 * every bind, so the effective style is whatever the mapper passed: Body1_DarkMedium
 * (16px #626262) for all three itinerary uses. Porting from the XML would give 14px.
 * Never truncated — unlike TextBaseActivity (4 lines) and DetailsHeader (10).
 */
function Body({ html }: { html: string }) {
  return (
    <AndroidText html={html} className="and-type-body1-dark-medium" style={marginStyle(BODY_MARGINS)} />
  );
}

/** SectionItem with style Title1 — 28px Bold, unlimited lines, MarginItem 16/8/16/16. */
function TitleSection({ text }: { text: string }) {
  return (
    <div
      className="and-type-title1"
      style={{ marginTop: 16, marginBottom: 8, marginLeft: 16, marginRight: 16, minHeight: 26 }}
    >
      {text}
    </div>
  );
}

/** item_row.xml with a calendar icon and showDivider = false. */
function CalendarRow({ text }: { text: string }) {
  return (
    <div style={{ height: 56, display: 'flex', alignItems: 'center' }}>
      <div
        style={{
          width: 40,
          height: 40,
          marginLeft: 8,
          padding: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--and-color-color-dark-medium)',
        }}
      >
        ▤
      </div>
      <div className="and-type-body1" style={{ marginLeft: 24, marginRight: 16, flex: 1, minWidth: 0 }}>
        {text}
      </div>
    </div>
  );
}

/** item_image_route.xml — 250dp tall, 8/16 margins, fitCenter.
 *
 * The one content image on any screen that is NOT centerCrop: a route map must not be
 * cropped. No radius, no placeholder rectangle.
 */
function RouteImage({
  image,
  ctx,
}: {
  image: { prefix: string; suffix: string };
  ctx: ItineraryItemContext;
}) {
  const src = imageUrl(ctx.imagesBase, image, ctx.imageWidthPx);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      style={{ width: '100%', height: 250, objectFit: 'contain', marginTop: 8, marginBottom: 16 }}
    />
  );
}

/** item_timeline_day.xml — the rail, a 24dp circle on a 1dp line at 24dp.
 *  REGULAR style: circle marginTop 10dp, marginBottom 16dp; text at 50dp. */
function TimelineDay({ text, position }: { text: string; position: TimelinePosition }) {
  const topLine = position === 'MIDDLE' || position === 'BOTTOM';
  const bottomLine = position === 'TOP' || position === 'MIDDLE';
  return (
    <div style={{ position: 'relative', minHeight: 50 }}>
      {topLine && <div style={{ ...railStyle, top: 0, height: 10 }} />}
      <div
        style={{
          position: 'absolute',
          left: 24 - 12,
          top: 10,
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: '1px solid var(--and-color-divider)',
        }}
      />
      {bottomLine && <div style={{ ...railStyle, top: 34, bottom: 0 }} />}
      {/* @style/Overline is allCaps, but the layout overrides textAllCaps=false —
          SemiBold 12px, MIXED case. */}
      <div
        className="and-type-overline"
        style={{
          marginLeft: 50,
          marginRight: 16,
          paddingTop: 10,
          paddingBottom: 16,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {text}
      </div>
    </div>
  );
}

const railStyle: React.CSSProperties = {
  position: 'absolute',
  left: 24,
  width: 1,
  background: 'var(--and-color-divider)',
};

/** item_timeline_day_content.xml — content at 40dp, rail behind it.
 *
 * The rail's visibility is the OPPOSITE convention to TimelineDayItem: visible for
 * TOP and MIDDLE, hidden for SINGLE and BOTTOM, so the line only ever runs downward.
 * The image card is flat (cardElevation 0dp), unlike the activity cards.
 * `footerView` has no LinkMovementMethod — footer links are blue but NOT tappable.
 */
function TimelineDayContent({
  image,
  subTitleHtml,
  bodyHtml,
  footerHtml,
  position,
  ctx,
}: {
  image: { prefix: string; suffix: string } | null;
  subTitleHtml: string | null;
  bodyHtml: string | null;
  footerHtml: string | null;
  position: TimelinePosition;
  ctx: ItineraryItemContext;
}) {
  const showRail = position === 'TOP' || position === 'MIDDLE';
  const src = image ? imageUrl(ctx.imagesBase, image, ctx.imageWidthPx) : null;
  return (
    <div style={{ position: 'relative' }}>
      {showRail && <div style={{ ...railStyle, top: 0, bottom: 0 }} />}
      <div style={{ marginLeft: 40, marginRight: 16 }}>
        {src && (
          <div style={{ height: 170, borderRadius: 16, overflow: 'hidden', marginBottom: 16 }}>
            <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}
        {subTitleHtml && (
          <AndroidText html={subTitleHtml} className="and-type-title3" style={{ marginBottom: 10 }} />
        )}
        {bodyHtml && <AndroidText html={bodyHtml} className="and-type-body2-dark" />}
        {footerHtml && <AndroidText html={footerHtml} className="and-type-body2-dark" />}
      </div>
    </div>
  );
}

export function ItineraryItemView({
  item,
  ctx,
}: {
  item: ItineraryItem;
  ctx: ItineraryItemContext;
}) {
  switch (item.kind) {
    case 'titleSection':
      return <TitleSection text={item.text} />;
    case 'body':
      return <Body html={item.html} />;
    case 'calendarRow':
      return <CalendarRow text={item.text} />;
    case 'routeImage':
      return <RouteImage image={item.image} ctx={ctx} />;
    case 'timelineDay':
      return <TimelineDay text={item.text} position={item.position} />;
    case 'timelineDayContent':
      return (
        <TimelineDayContent
          image={item.image}
          subTitleHtml={item.subTitleHtml}
          bodyHtml={item.bodyHtml}
          footerHtml={item.footerHtml}
          position={item.position}
          ctx={ctx}
        />
      );
    default:
      return null;
  }
}
